import TTLCache from '@isaacs/ttlcache';
import moment from 'moment';
import _ from 'lodash';
// Bypasses the shared/ui barrel: routing through it would create an import cycle, since the
// barrel also re-exports FabricJS/AIECGViewer, which imports @/entities/ecg.
import { Highpass, Lowpass } from '@/shared/ui/components/FabricJS/Helper/lowHighPassFilter';
import { KEY_CANCEL } from '@/shared/config';
import { filterHourlySummaryRanges } from '@/entities/heart-rate/@x/ecg';
import { downloadSelectedEcgData } from '../api/services/downloadSelectedEcgData';
import { addSineWaveToEcgData, convertBinaryToECGData } from './ecgConversion';

const CACHE_ECG = new TTLCache({ max: 800, ttl: 900000 }); // 15 minutes

/**
 * Substitutes a fallback for non-finite ECG samples. Returns the value when Number.isFinite accepts it and defaultValue otherwise.
 *
 * @param value - Sample to validate.
 * @param defaultValue - Replacement used when the sample is not finite.
 * @returns The value when finite, otherwise defaultValue.
 */
const ensureFiniteValue = (value: any, defaultValue = 0) =>
  Number.isFinite(value) ? value : defaultValue;

/**
 * Finds the median of an ECG channel, used as its baseline. Drops non-finite samples, returns zero when none remain, and otherwise sorts a copy and averages the two middle entries for an even count.
 *
 * @param data - Samples of one channel.
 * @returns The median of the finite samples, or zero when there are none.
 */
export const getMedian = (data: any[]) => {
  const finiteValues = data.filter(Number.isFinite);
  if (finiteValues.length === 0) {
    return 0;
  }
  const ordered = finiteValues.slice();
  ordered.sort((a: any, b: any) => a - b);
  const middle = Math.floor((ordered.length - 1) / 2);
  if (ordered.length % 2) {
    return ordered[middle];
  }
  return (ordered[middle] + ordered[middle + 1]) / 2.0;
};

/**
 * Keys a cached ECG download by the window it covers. Joins the start and stop values with a hyphen.
 *
 * @param start - Window start.
 * @param stop - Window stop.
 * @returns The cache key for the window.
 */
const generateId = (start: any, stop: any) => `${start}-${stop}`;

/**
 * Downloads and caches the ECG samples for a beat's window when the primary path could not serve them. Derives the window from momentObject, customOffset or offset around the floored beat epoch, returns the CACHE_ECG entry when that window was already fetched, otherwise resolves the hourly-summary ranges, downloads them, deinterleaves the buffer, splices sine waves into the gaps and caches the result, throwing when nothing came back.
 *
 * @param ecgDataMap - ECG map supplying channels, samplingFrequency and querySignature.
 * @param beat - Beat anchoring the window; its start parsed as a date, or its id, supplies the epoch.
 * @param offset - Symmetric half-width in milliseconds around the beat epoch.
 * @param momentObject - Explicit startMoment and stopMoment overriding the beat-derived window.
 * @param dataLength - Expected sample count per channel.
 * @param config - Optional customOffset pair giving asymmetric widths before and after the epoch.
 * @returns A promise resolving to the per-channel samples for the window.
 */
export const handleDownloadData = async ({
  ecgDataMap,
  beat,
  offset,
  momentObject = {} as any,
  dataLength,
  config,
}: {
  ecgDataMap: any;
  beat: any;
  offset: any;
  momentObject?: any;
  dataLength: any;
  config: any;
}) => {
  // *: dataLength = samplingRate * minutes
  const { customOffset } = config || {};

  let epoch = beat?.start ? new Date(beat.start).getTime() : beat?.id;

  // * Round epoch to get rounded bytes
  const dividedNumber = 1000 / ecgDataMap.samplingFrequency;
  epoch = Math.floor(epoch / dividedNumber) * dividedNumber;

  let startMoment: any;
  let stopMoment: any;
  if (!_.isEmpty(momentObject)) {
    // *: Draw selected event
    startMoment = momentObject.startMoment;
    stopMoment = momentObject.stopMoment;
  } else if (!_.isEmpty(customOffset)) {
    startMoment = moment(epoch - customOffset[0]);
    stopMoment = moment(epoch + customOffset[1]);
  } else if (offset) {
    startMoment = moment(epoch - offset);
    stopMoment = moment(epoch + offset);
  }
  const idAPI = generateId(startMoment.valueOf(), stopMoment.valueOf());
  if (CACHE_ECG.has(idAPI)) {
    return CACHE_ECG.get(idAPI);
  }
  const { channels, samplingFrequency } = ecgDataMap;
  const ranges = filterHourlySummaryRanges({
    summaries: ecgDataMap,
    start: startMoment,
    stop: stopMoment,
    channels,
    samplingFrequency,
    willAddDataRanges: true,
  });
  try {
    const downloadedSelectedEcgData = await downloadSelectedEcgData(
      ranges,
      ecgDataMap.querySignature,
      { cancelKey: KEY_CANCEL.DOWLOAD_ECG },
    );
    if (downloadedSelectedEcgData?.length) {
      // downloadSelectedEcgData returns any[] | Int16Array but convertBinaryToECGData (handler.ts, Task 7)
      // requires strictly Int16Array - pre-existing.
      const selectedEcgData = convertBinaryToECGData(
        channels.length,
        downloadedSelectedEcgData as any,
      );
      const addedSineWaveSelectedEcgData = addSineWaveToEcgData(
        selectedEcgData,
        ranges,
        startMoment,
        stopMoment,
        ecgDataMap,
        dataLength,
      );
      CACHE_ECG.set(idAPI, addedSineWaveSelectedEcgData);
      return addedSineWaveSelectedEcgData;
    }
    throw new Error('Error');
  } catch (error) {
    console.error('Error download data: ', error);
    throw error;
  }
};

let canClearCache = true;

/**
 * Empties the in-memory ECG download cache, at most twice a second. Bails out while the guard is down, otherwise clears CACHE_ECG and re-arms the guard after five hundred milliseconds.
 *
 * @returns Nothing.
 */
export const clearCacheAPI = () => {
  if (!canClearCache) {
    return;
  }
  canClearCache = false;
  CACHE_ECG.clear();
  setTimeout(() => {
    canClearCache = true;
  }, 500);
};

//* Preview strip
/**
 * Projects millivolt ECG samples onto canvas pixel coordinates per channel. Spreads the channels evenly down the available height and maps every sample to a floored x from its index and a y offset from its channel baseline by the millivolt scale.
 *
 * @param samples - Per-channel millivolt samples.
 * @param config - Drawing scale carrying height, millivoltToPix and indexToPix.
 * @returns Per-channel arrays of x and y pixel points.
 */
export const calculatePixelSamples = (samples: any[], config: any) => {
  const { height, millivoltToPix, indexToPix } = config;
  const samplesLength = samples.length;
  const verticalChannelPlacement = samples.map(
    (chArr: any, chIdx: number) => (height / samplesLength) * (chIdx + 0.5),
  );
  const pixelSamples = samples.map((chArr: any, chIdx: number) =>
    chArr.map((u: any, i: number) => ({
      x: Math.floor(i * indexToPix),
      y: Math.floor(verticalChannelPlacement[chIdx] - u * millivoltToPix),
    })),
  );
  return pixelSamples;
};

/**
 * Turns raw ADC counts into baseline-corrected, band-limited millivolt samples. Replaces non-finite counts with the ADC zero and scales by the gain, subtracts each channel's median, then unless skipFilter runs a highpass at LFF and a lowpass at HFF against the sampling rate.
 *
 * @param ecgData - Raw per-channel ADC counts.
 * @param config - Conversion settings: adcZero, adcGain, LFF, HFF and samplingRate.
 * @param skipFilter - When true, leaves the samples unfiltered after baseline removal.
 * @returns Per-channel millivolt samples.
 */
export const calculateSamples = (ecgData: any[], config: any, skipFilter = false) => {
  const { adcZero, adcGain, LFF, HFF, samplingRate } = config;
  const samples = ecgData.map((chData: any) =>
    chData.map((n: any) => (ensureFiniteValue(n, adcZero) - adcZero) / adcGain),
  );
  const B0 = samples.map(getMedian);
  const samplesLength = samples.length;
  samples.forEach((chArr: any, chIdx: number) => {
    const chArrLength = chArr.length;
    for (let j = 0; j < chArrLength; j += 1) {
      chArr[j] -= B0[chIdx];
    }
  });
  // *: Filter by 2Hz highpass and 20Hz lowpass
  if (!skipFilter) {
    for (let i = 0; i < samplesLength; i += 1) {
      samples[i] = Highpass(samples[i], LFF, samplingRate);
      samples[i] = Lowpass(samples[i], HFF, samplingRate);
    }
  }

  return samples;
};

export const calculateConfig = ({
  pGain,
  pSpeed,
  width,
  height,
  adcGain,
  ecgData,
  samplingRate,
  pLFF,
  pHFF,
}: {
  pGain?: number;
  pSpeed?: number;
  width: number;
  height: number;
  adcGain: any;
  ecgData: any[];
  samplingRate: any;
  pLFF?: number;
  pHFF?: any;
}) => {
  const adcZero = 0;
  const speed = pSpeed || 25;
  const gain = pGain || 2;
  const dataLength = ecgData[0]?.length;
  const recDuration = dataLength / samplingRate;
  const aspectRatioX = width / recDuration / speed;
  const aspectRatioY = aspectRatioX;
  const secondsToPix = speed * aspectRatioX;
  const indexToPix = (speed * aspectRatioX) / samplingRate;
  const majorDivSize = width / recDuration;
  const millivoltToPix = gain * aspectRatioY;
  const LFF = pLFF || 1;
  const HFF = !_.isNil(pHFF) ? pHFF : 40;
  return {
    adcGain,
    dataLength,
    recDuration,
    aspectRatioX,
    aspectRatioY,
    secondsToPix,
    indexToPix,
    majorDivSize,
    millivoltToPix,
    LFF,
    HFF,
    adcZero,
    width,
    height,
    samplingRate,
  };
};
